import { IModel, IAttribute, IViolation, IValidationContext } from '../base/annotation';
import { Metamodel } from '../base/model';
import { LinkedService } from './linkedservice';
import { AzureKeyVault } from './key-vault';
import { IAzureBlobLinkedServiceValidators } from './azure-blob-fs.linkedservice';
import { format } from '../../shared/utils/string.util';
import { Credential } from '../credential/credential';
import { getSqlPEResourceIDAndName } from '../../extensibility/sql.util';
import { IPrivateEndpointCompatible } from '../../interfaces/IPrivateEndpointCompatible';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { azureCloudTypeDef } from '../../constants/linkedservice-constants';
import { SQLAuthenticationType } from '../../interfaces/IAuthenticationType';
import { ModelConstants } from '../model-constants';
import { GlobalVariables } from '../../constants/globals';
import { SqlVersionType, SqlEncryptType, SqlAdditionalPropertiesKeyType } from '../../constants/sql-constants';
import { ParameterConfig } from '../pipeline/parameter-config';

export const azureSqlAuthRefTypes = {
    CredentialRefTypeAuthentication: 'credential',
    InlineRefTypeAuthentication: 'inline'
};

export const azureSqlLinkedServiceValidators: IAzureBlobLinkedServiceValidators = {
    validateAKVLinkedserviceReference: undefined
};

@Metamodel.model({
    name: ModelConstants.azureSqlDatabase,
    display: [
        format(shellStringResources.ModelName_LinkedServiceTemplate, shellStringResources.AzureSql_DisplayText),
        format(shellStringResources.ModelName_LinkedServicesTemplate, shellStringResources.AzureSql_DisplayText)
    ]
})
export class AzureSqlDatabaseLinkedService extends LinkedService implements IPrivateEndpointCompatible {

    public static readonly MODEL: IModel;

    public static readonly integratedSecurity: string = 'False';
    public static readonly encrypt: string = 'True';
    public static readonly connectionTimeout: string = '30';

    public static readonly SERVER_NAME: IAttribute;
    public static readonly DATABASE_NAME: IAttribute;
    public static readonly USER_NAME: IAttribute;
    public static readonly PASSWORD: IAttribute;
    public static readonly SERVICE_PRINCIPAL_ID: IAttribute;
    public static readonly SERVICE_PRINCIPAL_KEY: IAttribute;
    public static readonly TENANT: IAttribute;
    public static readonly ENCRYPTION_SERVICE_PRINCIPAL_ID: IAttribute;
    public static readonly ENCRYPTION_SERVICE_PRINCIPAL_KEY: IAttribute;
    public static readonly ENCRYPT: IAttribute;
    public static readonly TRUST_SERVER_CERTIFICATE: IAttribute;
    public static readonly HOST_NAME_IN_CERTIFICATE: IAttribute;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServerName,
        association: 'SERVER_NAME',
        templatable: true
    })
    public serverName?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_DatabaseName,
        association: 'DATABASE_NAME',
        templatable: true
    })
    public databaseName?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_UserName,
        association: 'USER_NAME',
        templatable: true
    })
    public userName?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_Password,
        constraint: {
            validator: (linkedservice: LinkedService, referencedResource: AzureKeyVault, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> => {
                if (azureSqlLinkedServiceValidators.validateAKVLinkedserviceReference) {
                    return azureSqlLinkedServiceValidators.validateAKVLinkedserviceReference(linkedservice, referencedResource, violation, context);
                }
                return undefined;
            }
        },
        association: 'PASSWORD',
        templatable: true,
        accepts: ['String']
    })
    public password?: string | AzureKeyVault;

    public enableAKV?: boolean = false;
    @Metamodel.attribute({
        constraint: {
            validator: (linkedservice: LinkedService, referencedResource: AzureKeyVault, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> => {
                if (azureSqlLinkedServiceValidators.validateAKVLinkedserviceReference) {
                    return azureSqlLinkedServiceValidators.validateAKVLinkedserviceReference(linkedservice, referencedResource, violation, context);
                }
                return undefined;
            }
        }
    })
    public referedAKV?: AzureKeyVault;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_AzureCloudType
    })
    public azureCloudType?: azureCloudTypeDef;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServicePrincipalID,
        association: 'SERVICE_PRINCIPAL_ID',
        templatable: true
    })
    public servicePrincipalId: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServicePrincipalKey,
        constraint: {
            validator: (linkedservice: LinkedService, referencedResource: AzureKeyVault, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> => {
                if (azureSqlLinkedServiceValidators.validateAKVLinkedserviceReference) {
                    return azureSqlLinkedServiceValidators.validateAKVLinkedserviceReference(linkedservice, referencedResource, violation, context);
                }
                return undefined;
            }
        },
        association: 'SERVICE_PRINCIPAL_KEY',
        templatable: true,
        accepts: ['String']
    })
    public servicePrincipalKey?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_Tenant,
        association: 'TENANT',
        templatable: true
    })
    public tenant: string;

    @Metamodel.attribute({
        display: shellStringResources.Credential,
        reference: true,
        constraint: {
            validator: AzureSqlDatabaseLinkedService.validateCredential
        }
    })
    public credential?: Credential;

    public authReferenceMethod?: string = azureSqlAuthRefTypes.InlineRefTypeAuthentication;

    @Metamodel.attribute({
        display: shellStringResources.AlwaysEncrypted
    })
    public alwaysEncrypted?: boolean;

    @Metamodel.attribute({
        display: shellStringResources.KeyStoreAuthType
    })
    public keyStoreAuthType: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServicePrincipalID,
        association: 'ENCRYPTION_SERVICE_PRINCIPAL_ID',
        templatable: true
    })
    public encryptionServicePrincipalId: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServicePrincipalKey,
        constraint: {
            validator: (linkedservice: LinkedService, referencedResource: AzureKeyVault, violation?: IViolation, context?: IValidationContext): Promise<IViolation[] | undefined> => {
                if (azureSqlLinkedServiceValidators.validateAKVLinkedserviceReference) {
                    return azureSqlLinkedServiceValidators.validateAKVLinkedserviceReference(linkedservice, referencedResource, violation, context);
                }
                return undefined;
            }
        },
        association: 'ENCRYPTION_SERVICE_PRINCIPAL_KEY',
        templatable: true,
        accepts: ['String']
    })
    public encryptionServicePrincipalKey?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_AuthenticationType
    })
    public authenticationType?: SQLAuthenticationType;

    @Metamodel.attribute({
        display: shellStringResources.Credentials,
        reference: true
    })
    public alwaysEncryptionCredential?: Credential;

    protected _serverResourceID?: string;

    public sqlVersion: SqlVersionType;

    //#region: properties in sql v2
    @Metamodel.attribute({
        display: shellStringResources.ModelName_Encrypt,
        association: 'ENCRYPT',
        templatable: true
    })
    public encrypt?: SqlEncryptType;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_TrustServerCertificate,
        association: 'TRUST_SERVER_CERTIFICATE',
        templatable: true
    })
    public trustServerCertificate?: boolean;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_HostNameInCertificate,
        association: 'HOST_NAME_IN_CERTIFICATE',
        templatable: true
    })
    public hostNameInCertificate?: string;

    public parameterizableAdditionalFields: Array<ParameterConfig<SqlAdditionalPropertiesKeyType>>;
    //#endregion

    constructor(name?: string, description?: string) {
        super(name, description);
    }

    private static validateCredential(linkedService: AzureSqlDatabaseLinkedService, referencedCredential: Credential): IViolation[] | undefined {
        if (linkedService &&
            ((linkedService.authenticationType === SQLAuthenticationType.ServicePrincipal &&
                linkedService.authReferenceMethod === azureSqlAuthRefTypes.CredentialRefTypeAuthentication) ||
                linkedService.authenticationType === SQLAuthenticationType.UserAssignedManagedIdentity ||
                (GlobalVariables.enableSamiSupport && linkedService.authenticationType === SQLAuthenticationType.ManagedServiceIdentity &&
                    linkedService.authReferenceMethod === azureSqlAuthRefTypes.CredentialRefTypeAuthentication) ||
                (GlobalVariables.enableConnectorUserAuthCred && linkedService.authenticationType === SQLAuthenticationType.UserAuth)) &&
            !referencedCredential) {
            return [{
                context: linkedService,
                message: () => shellStringResources.Error_InvalidCredentialReferenced
            }];
        }
        return undefined;
    }

    public getPEResourceIDAndName(): { id: string, name: string } {
        return getSqlPEResourceIDAndName(this.serverName, this._serverResourceID);
    }

    public setPEResourceID(id: string): void {
        this._serverResourceID = id;
    }
}
